import type { UndoStore } from '../store/undoStore';
import { getUndoStore } from '../store/undoStore';
import tombstoneOperations from 'owa-mail-list-tombstone';
import type { ActionSource } from 'owa-mail-store';
import type ApplyMessageActionResponseMessage from 'owa-service/lib/contract/ApplyMessageActionResponseMessage';
import type MoveItemResponse from 'owa-service/lib/contract/MoveItemResponse';
import { mutatorAction } from 'satcheljs';
import { PerformanceDatapoint } from 'owa-analytics';
import { isNotificationChannelInitialized } from 'owa-mail-notification-channel-ready/lib/isNotificationChannelInitialized';
import undoWhenNotificationChannelNotReady from 'owa-mail-actions/lib/table-loading/undoWhenNotificationChannelNotReady';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';

export interface UndoStoreState {
    store: UndoStore;
}

declare type UndoResponseType = ApplyMessageActionResponseMessage | MoveItemResponse | void;

/**
 * A single per-mailbox undo reverser. Cross-mailbox triage actions register
 * one action per mailbox group; single-mailbox actions register a one-action composite.
 */
export interface UndoAction {
    undo: () => Promise<UndoResponseType>;
    actionFolderId: string | null;
}

/**
 * Only supporting a "stack" of one (last undo action). For cross-mailbox actions this
 * holds multiple per-mailbox actions that are dispatched in parallel on undo.
 */
let lastUndoAction: UndoAction[] | null = null;

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
const setUndoableAction = mutatorAction('setUndoableAction', (hasUndoableActionToSet: boolean) => {
    const store = getUndoStore();
    store.hasUndoableAction = hasUndoableActionToSet;
});

export function addActionToUndoStack(
    undoAction: (() => Promise<UndoResponseType>) | null,
    actionFolderId: string | null
): void {
    if (undoAction !== null) {
        addCompositeActionToUndoStack([{ undo: undoAction, actionFolderId }]);
    }
}

/**
 * Registers a multi-action undo composite. Each action is reversed in parallel when the user undoes.
 * Used by cross-mailbox (UIB) triage actions to register one reverser per mailbox.
 */
export function addCompositeActionToUndoStack(actions: UndoAction[]): void {
    if (actions.length === 0) {
        return;
    }
    lastUndoAction = actions;
    setUndoableAction(true);
}

/**
 * Test-only accessor for the currently registered undo composite. Returns the
 * full array so tests can inspect every member; production code should call
 * `undo()` to run all reversers rather than peeking at the slot.
 * @return The composite of registered undo actions, or null if none is registered.
 */
export function getLastUndoActionForTest(): UndoAction[] | null {
    return lastUndoAction;
}

/**
 * Clears last undoable action (to null)
 */
export function clearLastUndoableAction(): void {
    lastUndoAction = null;
    setUndoableAction(false);
}

/**
 * Returns if last undoable action is empty
 * @return If last undoable action is empty
 */
export function hasUndoableAction(): boolean {
    return getUndoStore().hasUndoableAction;
}

/**
 * Undoes the last undoable action (if it exists).
 */
export function undo(actionSource: ActionSource): Promise<void> {
    const mailboxInfo = getModuleContextMailboxInfo();

    const dp = new PerformanceDatapoint('TnS_Undo', mailboxInfo, { isScorecardEvent: true });
    dp.addCustomData([actionSource]);

    const actions = lastUndoAction;

    // Need to remove everything for the folder where the undo action is being taken from tombstone because if the action is being performed on a row that is
    // in tombstone then we will not honor the row notification when the action gets undone so row won't come back.
    // For multi-mailbox undo, clear tombstones for each unique source folder.
    const folderIdsToClear = new Set<string>();
    if (actions) {
        for (const action of actions) {
            if (action.actionFolderId) {
                folderIdsToClear.add(action.actionFolderId);
            }
        }
    }
    if (folderIdsToClear.size > 0) {
        for (const folderId of folderIdsToClear) {
            tombstoneOperations.clearMapForFolderMutator(folderId);
        }
    } else {
        // There are cases when we do not have folder id e.g deleteItems which can be performed from various places like RP, fileshub etc.
        // Once (VSO: 45716 - [Refactor] Remove usage of geSelectedTableView from delete item) is fixed we shall pass correct folder id to clear
        tombstoneOperations.removeAllMutator();
    }

    // actions can be null if lazy loading of undo doesn't finish before user performs another action
    // that clears the undo stack.
    let undoChain: Promise<void> = Promise.resolve();
    if (actions !== null) {
        // Perform undo for every action in parallel.
        undoChain = Promise.allSettled(actions.map(action => action.undo())).then(() => {
            if (!isNotificationChannelInitialized()) {
                undoWhenNotificationChannelNotReady();
            }
        });

        // Must clear stack so user can't try to undo same action again. Also clears UI.
        clearLastUndoableAction();
    }
    dp.end();

    return undoChain;
}

/**
 * This is just exported for test purposes and should not be invoked in product code
 */
export function test_reset(): void {
    lastUndoAction = null;
}
